import { Box, Button, Divider, Skeleton, Tooltip, Typography } from '@mui/material'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo'
import EventIcon from '@mui/icons-material/Event'
import JournalLines from './JournalLines'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { IJournal } from '../../interface/IJournal'
import { IUser } from '../../interface/IUser'
import { format } from 'date-fns'
import { de } from 'date-fns/locale/de'
import { enUS } from 'date-fns/locale/en-US'
import axios, { AxiosRequestConfig } from 'axios'
import { useMsal } from '@azure/msal-react'
import { setAlert } from '../../source/slice/AlertSlice'
import { ICustomSnackbar } from '../../interface/ICustomSnackbar'
import { setJournalAbsenceBooked, setLoadingDate, setRequireCalendarReload } from '../../source/slice/LoadingDateSlice'
import { getAuthConfig } from '../../utils/authApi'
import { StatusChip } from '../../utils/StatusChip'
import React, { useEffect, useRef, useState } from 'react'
import { SpainAbsenceWarning } from '../Warnings/SpainAbsenceWarning'
import { ICalendar } from '@/interface/ICalendar'
import { UpdateStoreHelperJournal } from '@/source/StoreHelperJournal'
import Template from '../Template/Template'
import { applyTemplateToJournal, fetchTemplates, getTemplateHeader } from '@/utils/functions/templateUtils'
import { setTemplates } from '@/source/slice/TemplateSlice'
import { setSelectedJournal } from '@/source/slice/JournalSlice'
import { EmptyGuid } from '@/utils/values'

const JournalHeader = () => {
    const { t, i18n } = useTranslation()
    const journal: IJournal = useSelector((state: any) => state.journal?.value)
    const user: IUser = useSelector((state: any) => state.user?.value)
    const loadingDate: boolean = useSelector((state: any) => state.loadingDate?.value)
    const absenceBookedForSelectedJournalDay: boolean = useSelector((state: any) => state.loadingDate?.journalAbsenceBooked)
    const templates = useSelector((state: any) => state.template?.value) || []
    const { instance, accounts } = useMsal()
    const dispatch = useDispatch()
    const [absenceWarningOpen, setAbsenceWarningOpen] = useState<boolean>(false)
    const [openTemplateDialog, setOpenTemplateDialog] = useState<boolean>(false)
    const appliedDateRef = useRef<string | null>(null)
    const calendarDates: ICalendar[] = useSelector((state: any) => state.calendarDates?.value)
    const isDayOpen = !journal?.header?.mserp_posted
    const isFakUser = user?.worker?.mserp_employmentcategory === 'MY_INT' || user?.worker?.mserp_employmentcategory === 'MY_EXT' || user?.worker?.mserp_employmentcategory === 'TR_INT' || user?.worker?.mserp_employmentcategory === 'TR_EXT'
    const journalHeaderId = journal?.header?.mserp_projjournaltableentityid
    const hasNoJournalHeaderId = !journalHeaderId || journalHeaderId === EmptyGuid
    const currentCalendarDate = calendarDates?.find((date) => {
        const journalDate = journal?.header?.date
        if (!journalDate) return false
        const today = new Date(journalDate)
        const calendarDate = new Date(date.date)
        return calendarDate.getFullYear() === today.getFullYear() && calendarDate.getMonth() === today.getMonth() && calendarDate.getDate() === today.getDate()
    })
    const isAbsenceDay = currentCalendarDate?.status === 'ABSENCE' || absenceBookedForSelectedJournalDay
    const spainAbsence = user?.worker?.mserp_employmentcategory === 'ES_INT' && currentCalendarDate?.status === 'ABSENCE'
    useEffect(() => {
        if (spainAbsence) {
            setAbsenceWarningOpen(true)
        }
    }, [journal?.header?.date])

    useEffect(() => {
        const journalDate = journal?.header?.date
        dispatch(setJournalAbsenceBooked(false))

        if (!journalDate || !isFakUser || !instance || !accounts || !accounts[0]) {
            return
        }

        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    params: {
                        date: format(new Date(journalDate), 'yyyy-MM-dd'),
                    },
                }
                return axios.get(`${process.env.REACT_APP_BACKEND_URL}/FakAbsences`, config)
            })
            .then((response) => {
                const absences = Array.isArray(response?.data) ? response.data : []
                dispatch(setJournalAbsenceBooked(absences.length > 0))
            })
            .catch(() => {
                dispatch(setJournalAbsenceBooked(false))
            })
    }, [journal?.header?.date, isFakUser, instance, accounts, dispatch])

    useEffect(() => {
        let mounted = true

        const load = async () => {
            try {
                const data = await fetchTemplates(false)
                if (mounted) {
                    dispatch(setTemplates(data))
                }
            } catch (error) {
                console.log('Failed to load templates', error)
            }
        }

        if (instance && accounts && accounts[0]) {
            load()
        }

        return () => {
            mounted = false
        }
    }, [instance, accounts, dispatch])

    useEffect(() => {
        const journalDate = journal?.header?.date ? new Date(journal.header.date).toISOString() : null

        if (!journalDate || !isDayOpen || appliedDateRef.current === journalDate || templates.length === 0) {
            return
        }

        const defaultTemplate = templates.find((template: any) => getTemplateHeader(template)?.default)
        if (!defaultTemplate) {
            return
        }

        const applied = applyTemplateToJournal(journal, defaultTemplate, user)
        if (applied) {
            appliedDateRef.current = journalDate
            dispatch(setSelectedJournal(applied))
        }
    }, [journal, templates, isDayOpen, user, dispatch])

    const formatDate = () => {
        return i18n.language === 'de'
            ? format(new Date(journal?.header?.date ?? new Date().toISOString()), 'EEEE, dd.MM.yyyy', {
                  locale: de,
              })
            : format(new Date(journal?.header?.date ?? new Date().toISOString()), 'EEEE, MM/dd/yyyy', {
                  locale: enUS,
              })
    }

    const customSnackbarMessageSuccess: ICustomSnackbar = {
        message: t('zdeheader.deleteSuccess'),
        type: 'success',
    }

    const customSnackbarMessageError: ICustomSnackbar = {
        message: t('zdeheader.deleteError'),
        type: 'error',
    }

    const deleteJournal = () => {
        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    params: {
                        date: journal?.header?.date ? format(journal.header.date, 'yyyy-MM-dd') : '',
                    },
                }
                dispatch(setLoadingDate(true))
                axios
                    .delete(`${process.env.REACT_APP_BACKEND_URL}/Journal`, config)
                    .then(async (response) => {
                        if (response.status === 200) {
                            dispatch(setAlert(customSnackbarMessageSuccess))
                            if (journal.header?.date) {
                                await UpdateStoreHelperJournal(journal.header?.date, true)
                            } else {
                                dispatch(setLoadingDate(false))
                            }
                            dispatch(setRequireCalendarReload(true))
                        } else {
                            dispatch(setAlert(customSnackbarMessageError))
                            dispatch(setLoadingDate(false))
                        }
                        console.log(response)
                    })
                    .catch((error) => {
                        console.log(error)
                        dispatch(setLoadingDate(false))
                    })
            })
            .catch(() => {
                dispatch(setAlert(customSnackbarMessageError))
                dispatch(setLoadingDate(false))
            })
    }

    const customSnackbarMessageAbsenceSuccess: ICustomSnackbar = {
        message: t('zdeheader.absenceBookingSuccess'),
        type: 'success',
    }

    const customSnackbarMessageAbsenceError: ICustomSnackbar = {
        message: t('zdeheader.absenceBookingError'),
        type: 'error',
    }

    const customSnackbarMessageAbsenceCancelSuccess: ICustomSnackbar = {
        message: t('zdeheader.absenceCancelSuccess'),
        type: 'success',
    }

    const customSnackbarMessageAbsenceCancelError: ICustomSnackbar = {
        message: t('zdeheader.absenceCancelError'),
        type: 'error',
    }

    const createAbsenceBooking = () => {
        const journalDate = journal?.header?.date

        if (!journalDate || !hasNoJournalHeaderId || isAbsenceDay) {
            return
        }

        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = { ...headers }

                dispatch(setLoadingDate(true))
                axios
                    .post(
                        `${process.env.REACT_APP_BACKEND_URL}/FakAbsences`,
                        JSON.stringify({
                            mbti_date: format(new Date(journalDate), 'yyyy-MM-dd'),
                        }),
                        config
                    )
                    .then(async (response) => {
                        if (response.status === 200) {
                            dispatch(setAlert(customSnackbarMessageAbsenceSuccess))
                            dispatch(setJournalAbsenceBooked(true))
                            dispatch(setRequireCalendarReload(true))
                            await UpdateStoreHelperJournal(journal.header!.date, true)
                        } else {
                            dispatch(setAlert(customSnackbarMessageAbsenceError))
                        }
                    })
                    .catch(() => {
                        dispatch(setAlert(customSnackbarMessageAbsenceError))
                    })
                    .finally(() => {
                        dispatch(setLoadingDate(false))
                    })
            })
            .catch(() => {
                dispatch(setAlert(customSnackbarMessageAbsenceError))
                dispatch(setLoadingDate(false))
            })
    }

    const cancelAbsenceBooking = () => {
        const journalDate = journal?.header?.date

        if (!journalDate || !isAbsenceDay) {
            return
        }

        getAuthConfig(instance, accounts[0])
            .then((headers) => {
                const config: AxiosRequestConfig = {
                    ...headers,
                    params: {
                        date: format(new Date(journalDate), 'yyyy-MM-dd'),
                    },
                }

                dispatch(setLoadingDate(true))
                axios
                    .delete(`${process.env.REACT_APP_BACKEND_URL}/FakAbsences`, config)
                    .then(async (response) => {
                        if (response.status === 200) {
                            dispatch(setAlert(customSnackbarMessageAbsenceCancelSuccess))
                            dispatch(setJournalAbsenceBooked(false))
                            dispatch(setRequireCalendarReload(true))
                            await UpdateStoreHelperJournal(journal.header!.date, true)
                        } else {
                            dispatch(setAlert(customSnackbarMessageAbsenceCancelError))
                        }
                    })
                    .catch(() => {
                        dispatch(setAlert(customSnackbarMessageAbsenceCancelError))
                    })
                    .finally(() => {
                        dispatch(setLoadingDate(false))
                    })
            })
            .catch(() => {
                dispatch(setAlert(customSnackbarMessageAbsenceCancelError))
                dispatch(setLoadingDate(false))
            })
    }

    return (
        <>
            <SpainAbsenceWarning isOpen={absenceWarningOpen} onConfirm={() => setAbsenceWarningOpen(false)} />
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: '16px', justifyContent: 'space-between', width: '100%', '@media (min-width: 700px)': { flexDirection: 'row', alignItems: 'end', gap: '0' } }}>
                {!loadingDate && user ? (
                    <Typography variant="h4">
                        {formatDate()} | ({user?.resource?.mserp_resourceid}) <StatusChip status={journal?.header?.status ?? ''} />
                    </Typography>
                ) : (
                    <Typography variant="h4">
                        <Skeleton variant="text" sx={{ width: { xs: 200, md: 300 }, display: 'inline-block' }} />
                    </Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                    {journal?.header?.date && user?.worker?.mserp_employmentcategory !== 'FAK' && (
                        <Tooltip title={t('template.tooltip_title')}>
                            <Button variant="outlined" onClick={() => setOpenTemplateDialog(true)} startIcon={<ContentPasteGoIcon />} color="primary" disabled={isAbsenceDay}>
                                {t('template.title')}
                            </Button>
                        </Tooltip>
                    )}
                    {journal?.header?.date && isFakUser && (
                        <Tooltip title={t('zdeheader.absenceBookingTitle')}>
                            <span>
                                <Button
                                    variant="outlined"
                                    onClick={isAbsenceDay ? cancelAbsenceBooking : createAbsenceBooking}
                                    sx={{ color: 'rgb(140, 43, 167)', borderColor: 'rgb(107, 0, 138)' }}
                                    disabled={!hasNoJournalHeaderId || loadingDate || !isDayOpen}
                                    startIcon={<EventIcon />}
                                >
                                    {isAbsenceDay ? t('zdeheader.absenceCancel') : t('zdeheader.absenceBooking')}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    <Button variant="outlined" onClick={deleteJournal} startIcon={<RestartAltIcon />} color="error" disabled={journal?.header?.mserp_posted || isAbsenceDay}>
                        {t('zdeheader.resetDay')}
                    </Button>
                </Box>
            </Box>
            {openTemplateDialog && <Template onClose={() => setOpenTemplateDialog(false)} isDayOpen={isDayOpen} />}
            <Divider sx={{ marginTop: '16px' }} />
            <JournalLines />
        </>
    )
}

export default JournalHeader
